function normalizeIdValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return String(value).trim();
  if (typeof value === 'object') {
    return String(value._id || value.id || value.value || '').trim();
  }
  return String(value).trim();
}

function normalizeIdList(value) {
  const list = Array.isArray(value) ? value : [];
  return Array.from(
    new Set(
      list
        .map((item) => normalizeIdValue(item))
        .filter(Boolean),
    ),
  );
}

function parseIdList(value) {
  if (Array.isArray(value)) {
    return normalizeIdList(value);
  }

  if (value === null || value === undefined) return [];

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return normalizeIdList(parsed);
      }
    } catch (error) {
      // fall through to delimiter parsing
    }
    return Array.from(
      new Set(
        raw
          .split(/[|,]/)
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    );
  }

  return normalizeIdList([value]);
}

const COMPATIBILITY_STOP_WORDS = new Set([
  'and',
  'or',
  'the',
  'for',
  'with',
  'from',
  'this',
  'that',
  'compatible',
  'compatibility',
  'general',
  'product',
  'products',
  'part',
  'parts',
  'spare',
  'spareparts',
  'unclassified',
  'none',
]);

function normalizeCompatibilityTerm(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_/-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveLocalizedCompatibilityValue(value, language) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const lang = String(language || 'en').toLowerCase() === 'fr' ? 'fr' : 'en';
    return String(value[lang] || value.en || value.fr || '').trim();
  }
  return String(value || '').trim();
}

function addCompatibilityTerm(target, value, weight = 1) {
  const term = normalizeCompatibilityTerm(value);
  if (!term || term.length < 2 || COMPATIBILITY_STOP_WORDS.has(term)) return;
  const existing = target.get(term);
  if (existing == null || weight > existing) {
    target.set(term, weight);
  }
}

function collectCompatibilityTerms(value, target, weight = 1, depth = 0) {
  if (!target || depth > 4 || value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectCompatibilityTerms(item, target, weight, depth + 1));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectCompatibilityTerms(item, target, weight, depth + 1));
    return;
  }
  addCompatibilityTerm(target, value, weight);
}

function resolveCompatibilityBrand(item = {}) {
  const specs = item?.specs && typeof item.specs === 'object' && !Array.isArray(item.specs) ? item.specs : {};
  return String(specs.brand || specs.manufacturer || specs.make || specs.model || item?.brand || '').trim();
}

function buildProductCompatibilitySignals(product = {}, options = {}) {
  const language = String(options.language || 'en').toLowerCase() === 'fr' ? 'fr' : 'en';
  const signals = new Map();

  addCompatibilityTerm(signals, product.name, 2);
  addCompatibilityTerm(signals, product.slug, 1);
  addCompatibilityTerm(signals, product.description, 1);
  addCompatibilityTerm(signals, product.productType, 4);
  addCompatibilityTerm(signals, options.productTypeLabel, 4);
  addCompatibilityTerm(signals, resolveLocalizedCompatibilityValue(options.productTypeLabelI18n, language), 4);
  addCompatibilityTerm(signals, resolveCompatibilityBrand(product), 5);
  addCompatibilityTerm(signals, product.brand, 5);
  addCompatibilityTerm(signals, product.manufacturer, 5);
  addCompatibilityTerm(signals, product.make, 5);
  addCompatibilityTerm(signals, product.model, 4);
  addCompatibilityTerm(signals, product?.translations?.[language]?.name, 2);
  addCompatibilityTerm(signals, product?.translations?.[language]?.description, 1);
  addCompatibilityTerm(signals, product?.specs?.brand, 5);
  addCompatibilityTerm(signals, product?.specs?.manufacturer, 5);
  addCompatibilityTerm(signals, product?.specs?.make, 5);
  addCompatibilityTerm(signals, product?.specs?.model, 4);
  collectCompatibilityTerms(product?.specs, signals, 2);
  collectCompatibilityTerms(product?.specsI18n?.[language], signals, 2);

  return signals;
}

function buildSparePartCompatibilitySignals(sparePart = {}, options = {}) {
  const language = String(options.language || 'en').toLowerCase() === 'fr' ? 'fr' : 'en';
  const signals = new Map();

  addCompatibilityTerm(signals, sparePart.name, 2);
  addCompatibilityTerm(signals, sparePart.slug, 1);
  addCompatibilityTerm(signals, sparePart.description, 1);
  addCompatibilityTerm(signals, sparePart.category, 4);
  addCompatibilityTerm(signals, options.categoryLabel, 4);
  addCompatibilityTerm(signals, resolveLocalizedCompatibilityValue(options.categoryLabelI18n, language), 4);
  addCompatibilityTerm(signals, sparePart?.translations?.[language]?.name, 2);
  addCompatibilityTerm(signals, sparePart?.translations?.[language]?.description, 1);
  addCompatibilityTerm(signals, sparePart?.categoryI18n?.[language], 4);

  return signals;
}

function buildProductCompatibilityTerms(product = {}, options = {}) {
  return Array.from(buildProductCompatibilitySignals(product, options).keys());
}

function buildSparePartCompatibilityTerms(sparePart = {}, options = {}) {
  return Array.from(buildSparePartCompatibilitySignals(sparePart, options).keys());
}

function scoreCompatibilityMatch(source, candidate, options = {}) {
  const sourceKind = String(options.sourceKind || 'product').toLowerCase();
  const candidateKind = String(options.candidateKind || '').toLowerCase();
  const sourceSignals =
    sourceKind === 'sparepart'
      ? buildSparePartCompatibilitySignals(source, options)
      : buildProductCompatibilitySignals(source, options);
  const candidateTerms = new Set(
    (candidateKind === 'product'
      ? buildProductCompatibilityTerms(candidate, options)
      : candidateKind === 'sparepart'
        ? buildSparePartCompatibilityTerms(candidate, options)
        : [
            ...buildProductCompatibilityTerms(candidate, options),
            ...buildSparePartCompatibilityTerms(candidate, options),
          ]
    ).filter(Boolean),
  );
  const candidateText = Array.from(candidateTerms).join(' | ');
  let score = 0;

  sourceSignals.forEach((weight, term) => {
    if (candidateText.includes(term)) {
      score += weight;
    }
  });

  return score;
}

function rankCompatibilityCandidates(source, candidates, options = {}) {
  const candidateKind = String(options.candidateKind || '').toLowerCase();
  const language = String(options.language || 'en');
  const limit = Math.max(1, Number(options.limit) || 6);
  const sourceKind = String(options.sourceKind || 'product').toLowerCase();
  const list = Array.isArray(candidates) ? candidates : [];

  return list
    .map((candidate) => {
      const score = scoreCompatibilityMatch(source, candidate, {
        ...options,
        sourceKind: sourceKind || 'product',
        candidateKind: candidateKind || 'sparepart',
        language,
      });
      return { item: candidate, score };
    })
    .filter(({ item, score }) => Boolean(item) && score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      const leftName =
        sourceKind === 'sparepart'
          ? resolveProductName(left.item, language)
          : resolveSparePartName(left.item, language);
      const rightName =
        sourceKind === 'sparepart'
          ? resolveProductName(right.item, language)
          : resolveSparePartName(right.item, language);
      return String(leftName || '').localeCompare(String(rightName || ''), undefined, { sensitivity: 'base' });
    })
    .slice(0, limit)
    .map(({ item }) => item);
}

function resolveSparePartName(item = {}, language = 'en') {
  const localized = item?.translations?.[language] || {};
  const baseName = String(item?.name || '').trim();
  return String(localized.name || baseName).trim();
}

function resolveSparePartCategory(item = {}, language = 'en') {
  const localizedCategory = item?.categoryI18n?.[language];
  return String(localizedCategory || item?.category || item?.categoryKey || '').trim();
}

function resolveProductName(item = {}, language = 'en') {
  const localized = item?.translations?.[language] || {};
  const baseName = String(item?.name || '').trim();
  return String(localized.name || baseName).trim();
}

module.exports = {
  buildProductCompatibilityTerms,
  buildSparePartCompatibilityTerms,
  normalizeIdList,
  normalizeIdValue,
  parseIdList,
  rankCompatibilityCandidates,
  scoreCompatibilityMatch,
  resolveProductName,
  resolveSparePartCategory,
  resolveSparePartName,
};
